import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from 'helpers/theme';
import { createFragmentContainer, graphql } from 'react-relay';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCamera,
  faSpinner,
  faXmark
} from '@fortawesome/pro-regular-svg-icons';
import CoreInput, { InputTypes } from 'sharedComponents/core/Input/CoreInput';
import Button from 'components/Button/Button';
import { uploadProfileImage } from 'components/Delegate/DelegateModal/AvatarUpload';
import { useModalRegistration } from 'views/ModalRegistryProvider';
import {
  toInitials,
  initialToColour
} from 'sharedComponents/core/ProfileIcon/ProfileIcon';
import headerCheckerboard from 'assets/logo/header-checkerboard-company.svg';
import { UpdateCompany } from './mutations';
import { CompanyModal_company } from './__generated__/CompanyModal_company.graphql';

const useStyles = createUseStyles((theme: Theme) => ({
  '@keyframes overlayIn': {
    from: { opacity: 0 },
    to: { opacity: 1 }
  },
  '@keyframes panelIn': {
    from: { opacity: 0, transform: 'scale(0.94) translateY(24px)' },
    to: { opacity: 1, transform: 'scale(1) translateY(0)' }
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingTop: '7vh',
    background: 'rgba(23, 25, 35, 0.18)',
    animation: '$overlayIn 0.15s ease-out'
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    width: 480,
    maxWidth: 'calc(100vw - 40px)',
    maxHeight: '86vh',
    background: 'white',
    borderRadius: 14,
    border: '1px solid #ededed',
    boxShadow: '0 16px 70px rgba(0, 0, 0, 0.2), 0 3px 12px rgba(0, 0, 0, 0.08)',
    animation: '$panelIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
    overflow: 'hidden'
  },
  cover: {
    position: 'relative',
    flexShrink: 0,
    height: 84,
    background: `
      url(${headerCheckerboard}) left top / auto 100% no-repeat,
      linear-gradient(135deg, #f4f7ff, #eef3ff)
    `,
    borderBottom: '1px solid #ededed'
  },
  closeButton: {
    position: 'absolute',
    top: 14,
    right: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
    height: 30,
    borderRadius: '50%',
    background: 'white',
    border: '1px solid #ededed',
    cursor: 'pointer',
    color: '#74777f',
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor: '#f0f1f3'
    }
  },
  avatarRow: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: -34,
    zIndex: 1
  },
  logoWrap: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    height: 68,
    minWidth: 96,
    maxWidth: 220,
    padding: '0 18px',
    background: 'white',
    borderRadius: 12,
    boxShadow: '0 0 0 4px white, 0 2px 8px rgba(0, 0, 0, 0.08)'
  },
  cameraBadge: {
    position: 'absolute',
    right: -8,
    bottom: -8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: '#0b57ff',
    border: '2px solid white',
    color: 'white',
    fontSize: 12,
    cursor: 'pointer',
    transition: 'background-color 0.15s, transform 0.15s',
    '&:hover': {
      backgroundColor: '#0a4cdf',
      transform: 'scale(1.08)'
    }
  },
  fileInput: {
    display: 'none'
  },
  logoImg: {
    maxWidth: 180,
    maxHeight: 46
  },
  logoFallback: {
    fontSize: 20,
    fontWeight: 900,
    color: '#00000080',
    letterSpacing: 1
  },
  titleBlock: {
    padding: '12px 24px 18px',
    textAlign: 'center'
  },
  title: {
    fontSize: 17,
    fontWeight: 700,
    color: theme.colors.primaryBlack
  },
  subtitle: {
    marginTop: 4,
    fontSize: 13,
    color: '#74777f'
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 24px 24px'
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#9ea2ad',
    margin: '8px 0 10px'
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 16
  },
  fieldGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: 14,
    '& > $field': {
      marginBottom: 0
    },
    marginBottom: 16
  },
  label: {
    fontSize: 13,
    fontWeight: 500,
    color: '#74777f',
    marginBottom: 7
  },
  input: {
    boxSizing: 'border-box',
    minHeight: 40,
    padding: '0 14px',
    fontSize: 14,
    color: theme.colors.primaryBlack,
    background: 'white',
    border: '1px solid #ededed',
    borderRadius: 8,
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:focus': {
      borderColor: '#0b57ff',
      boxShadow: '0 0 0 3px rgba(11, 87, 255, 0.1)'
    },
    '&::placeholder': {
      color: '#9ea2ad'
    }
  },
  errorBanner: {
    padding: '12px 14px',
    marginBottom: 14,
    fontSize: 13,
    color: '#b3261e',
    background: '#fef1f2',
    border: '1px solid #fadcdf',
    borderRadius: 8
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gridGap: 10,
    padding: '16px 24px',
    borderTop: '1px solid #ededed'
  }
}));

type CompanyFields = {
  name: string;
  contactEmail: string;
  addressLine1: string;
  addressLine2: string;
  county: string;
  postCode: string;
  logoUrl?: string;
  logoToken?: string;
};

type Field = {
  label: string;
  placeholder: string;
  key: keyof CompanyFields;
  type?: InputTypes;
};

const companyFields: Field[] = [
  { label: 'Company Name', placeholder: 'Company Name', key: 'name' },
  {
    label: 'Company Contact Email',
    placeholder: 'Company Email',
    key: 'contactEmail',
    type: 'email'
  }
];

const addressFields: Field[] = [
  {
    label: 'Address Line 1',
    placeholder: 'Address Line 1',
    key: 'addressLine1'
  },
  {
    label: 'Address Line 2',
    placeholder: 'Address Line 2',
    key: 'addressLine2'
  }
];

const addressGridFields: Field[] = [
  { label: 'County', placeholder: 'County', key: 'county' },
  { label: 'Post Code', placeholder: 'Post Code', key: 'postCode' }
];

type Props = {
  isOpen: boolean;
  company: CompanyModal_company;
  refetch: () => void;
  onClose: () => void;
};

const CompanyModal = ({ isOpen, company, refetch, onClose }: Props) => {
  const theme = useTheme();
  const classes = useStyles({ theme });
  useModalRegistration(isOpen);

  const companyToFields = (): CompanyFields => ({
    name: company.name,
    contactEmail: company.contactEmail ?? '',
    addressLine1: company.address.addressLine1,
    addressLine2: company.address.addressLine2 ?? '',
    county: company.address.county,
    postCode: company.address.postCode,
    logoUrl: company.logoURL ?? undefined
  });

  const [fields, setFields] = React.useState<CompanyFields>(companyToFields);
  const [loading, setLoading] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Reset the form whenever the modal opens
  React.useEffect(() => {
    if (!isOpen) return;
    setFields(companyToFields());
    setLoading(false);
    setUploading(false);
    setError(undefined);
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (evt: KeyboardEvent) => {
      if (evt.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const setField = (key: keyof CompanyFields) => (value: string) =>
    setFields((f) => ({ ...f, [key]: value }));

  // Limitation: saving always submits the whole form as shown — there is no
  // changed-field tracking, so unchanged fields are resent too.
  const submit = () => {
    if (loading) return;
    setLoading(true);
    setError(undefined);
    UpdateCompany(
      {
        uuid: company.uuid,
        name: fields.name,
        contactEmail: fields.contactEmail,
        addressLine1: fields.addressLine1,
        addressLine2: fields.addressLine2,
        county: fields.county,
        postCode: fields.postCode,
        logoToken: fields.logoToken
      },
      (err: string) => {
        setError(err);
        setLoading(false);
      },
      () => {
        setLoading(false);
        refetch();
        onClose();
      }
    );
  };

  const renderField = ({ label, placeholder, key, type }: Field) => (
    <div className={classes.field} key={key}>
      <span className={classes.label}>{label}</span>
      <CoreInput
        type={type ?? 'text'}
        placeholder={placeholder}
        value={fields[key] ?? ''}
        onChange={setField(key)}
        className={classes.input}
      />
    </div>
  );

  const fallbackTint = initialToColour(toInitials(fields.name)) + '60';

  return (
    <div className={classes.overlay} onMouseDown={onClose}>
      <div
        className={classes.panel}
        onMouseDown={(evt) => evt.stopPropagation()}
      >
        <div className={classes.cover}>
          <div className={classes.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </div>
        </div>
        <div className={classes.avatarRow}>
          <div
            className={classes.logoWrap}
            style={{
              // Tint layered over the white base — the colour is translucent
              // and would otherwise show the cover header through the card
              backgroundImage: fields.logoUrl
                ? undefined
                : `linear-gradient(${fallbackTint}, ${fallbackTint})`
            }}
          >
            {fields.logoUrl ? (
              <img
                src={fields.logoUrl}
                alt={fields.name}
                className={classes.logoImg}
              />
            ) : (
              <span className={classes.logoFallback}>
                {toInitials(fields.name)}
              </span>
            )}
            <div
              className={classes.cameraBadge}
              title="Upload logo"
              onClick={() =>
                fileInputRef.current && fileInputRef.current.click()
              }
            >
              <FontAwesomeIcon
                icon={uploading ? faSpinner : faCamera}
                spin={uploading}
              />
            </div>
            <input
              accept="image/*"
              className={classes.fileInput}
              type="file"
              ref={fileInputRef}
              onChange={(evt) => {
                const file = evt.target.files && evt.target.files[0];
                if (!file) return;
                setUploading(true);
                setError(undefined);
                uploadProfileImage(
                  file,
                  (err) => {
                    setUploading(false);
                    setError(err);
                  },
                  (token, url) => {
                    setUploading(false);
                    setFields((f) => ({
                      ...f,
                      logoToken: token,
                      logoUrl: url
                    }));
                  }
                );
              }}
            />
          </div>
        </div>
        <div className={classes.titleBlock}>
          <div className={classes.title}>{fields.name || 'Company'}</div>
          <div className={classes.subtitle}>Update your company details</div>
        </div>
        <div className={classes.body}>
          {error && (
            <div className={classes.errorBanner}>
              Unable to save details: {error}
            </div>
          )}
          <div className={classes.sectionLabel}>Company</div>
          {companyFields.map(renderField)}
          <div className={classes.sectionLabel}>Billing Address</div>
          {addressFields.map(renderField)}
          <div className={classes.fieldGrid}>
            {addressGridFields.map(renderField)}
          </div>
        </div>
        <div className={classes.footer}>
          <Button archetype="flat" onClick={onClose}>
            Cancel
          </Button>
          <Button
            archetype="flatPrimary"
            disabled={loading || uploading}
            onClick={submit}
          >
            {loading ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default createFragmentContainer(CompanyModal, {
  company: graphql`
    fragment CompanyModal_company on Company {
      uuid
      name
      contactEmail
      logoURL
      address {
        addressLine1
        addressLine2
        county
        postCode
      }
    }
  `
});
